import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Calendar, LayoutDashboard, PlusSquare, BarChart3,
  Users, LogOut, ChevronDown, User, Menu, X, ShieldCheck
} from 'lucide-react';

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const closeAll = () => { setMobileOpen(false); setDropdownOpen(false); };

  const navItems = [
    { to: '/', label: 'Dashboard', icon: <LayoutDashboard size={16} />, end: true },
    { to: '/jadwal', label: 'Lihat Jadwal', icon: <Calendar size={16} /> },
    { to: '/rekap', label: 'Rekap Bulanan', icon: <BarChart3 size={16} /> },

    ...(isAdmin ? [
      { to: '/input', label: 'Input Jadwal', icon: <PlusSquare size={16} /> }, 
      { to: '/users', label: 'Kelola User', icon: <Users size={16} /> }
    ] : []),
  ];

  const today = new Date().toLocaleDateString('id-ID', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  return (
    <>
      <nav className="navbar">
        {/* Brand */}
        <NavLink to="/" className="navbar-brand" onClick={closeAll}>
          <div className="navbar-logo">
            <img
              src="/images/jayaraya.png"
              alt="Logo Jaya Raya"
              style={{
                width: 50,
                height: 50,
                objectFit: 'contain'
              }}
            />
          </div>
          <div>
            <div className="navbar-title">Penjadwalan</div>
            <div className="navbar-sub">Acara Pimpinan</div>
          </div>
        </NavLink>

        {/* Desktop nav */}
        <div className="navbar-nav desktop-only">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
              {item.icon} {item.label}
            </NavLink>
          ))}
        </div>

        {/* Kanan: tanggal + user */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="desktop-only" style={{ fontSize: 12, color: 'var(--gray-400)', fontWeight: 500 }}>
            {today}
          </span>

          {/* Dropdown user */}
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button className="user-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
              <div className="user-avatar">{user?.nama?.[0]?.toUpperCase()}</div>
              <div className="desktop-only" style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                  {user?.role === 'admin' ? 'Administrator' : 'Staff'}
                </div>
              </div>
              <ChevronDown size={14} color="var(--gray-400)" className="desktop-only" />
            </button>

            {dropdownOpen && (
              <div className="dropdown-menu">
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-100)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{user?.nama}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{user?.email}</div>
                  <span className={`badge ${user?.role === 'admin' ? 'badge-orange' : 'badge-blue'}`}
                    style={{ marginTop: 6, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {user?.role === 'admin'
                      ? <><ShieldCheck size={10} /> Admin</>
                      : <><User size={10} /> Staff</>}
                  </span>
                </div>
                <button className="dropdown-item" onClick={() => { closeAll(); navigate('/profile'); }}>
                  <User size={15} /> Profil Saya
                </button>
                <button className="dropdown-item" style={{ color: 'var(--red)' }} onClick={handleLogout}>
                  <LogOut size={15} /> Keluar
                </button>
              </div>
            )}
          </div>

          {/* Hamburger mobile */}
          <button className="hamburger mobile-only" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile overlay menu */}
      {mobileOpen && (
        <div className="mobile-menu">
          <div style={{ padding: '12px 20px 8px', borderBottom: '1px solid var(--gray-100)', marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{user?.nama}</div>
            <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{user?.email}</div>
          </div>
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
              onClick={closeAll}>
              {item.icon} {item.label}
            </NavLink>
          ))}
          <div style={{ borderTop: '1px solid var(--gray-100)', margin: '8px 0' }} />
          <button className="mobile-nav-link"
            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={() => { closeAll(); navigate('/profile'); }}>
            <User size={16} /> Profil Saya
          </button>
          <button className="mobile-nav-link"
            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--red)' }}
            onClick={handleLogout}>
            <LogOut size={16} /> Keluar
          </button>
        </div>
      )}
    </>
  );
}
